// 新增 ONVIF 攝影機（plan/vms-web-v0.md §5.4）：一律經現場的 Kinio 閘道（NVR / station）。
//   1. 選閘道 → 探索（閘道做 WS-Discovery；平台做自家設備判定）
//   2. 四組：已納管 / 自家設備（只給 Kinio 配對）/ 疑似自家設備（預設 Kinio 配對；owner 可填理由硬以 ONVIF 加入）/ 第三方
//   3. 勾選第三方、輸入帳密（只送到閘道，平台不存）→ 逐台顯示結果（auth_failed 與 unsupported 分開）
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { useDevices } from '../../lib/useDevices';
import { useScope } from '../../lib/scope';
import { isChannelHost } from '../playback/view';
import { onvifApi, type DiscoveredCam, type DiscoveryResult, type OnvifAddResult } from '../../lib/planeApi';
import { type VmsDevice } from '../../lib/devices';
import { canSetChannels } from './gatewayAccess';
import { PasswordInput } from '../../components/PasswordInput';

/** 能當 ONVIF 閘道的：通道主機（有通道、本地錄影主機、device_class nvr / gateway；vms-web §5.19.1）。 */
export function gatewaysOf(tree: VmsDevice[]): VmsDevice[] {
  return tree.filter(isChannelHost);
}

const GROUPS = ['third_party', 'suspect', 'own', 'managed'] as const;

export function OnvifDialog({ open, close }: { open: boolean; close(): void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { tree } = useDevices();
  const { homes, orgs } = useScope();
  // ONVIF 納管是管理動作：家戶 owner / admin，或企業家戶的 owner / org_admin / org.sites（同平台 onvifScope）
  const gateways = useMemo(() => gatewaysOf(tree).filter((d) => canSetChannels(d, homes, orgs)), [tree, homes, orgs]);
  const [gwKey, setGwKey] = useState('');
  const gw = gateways.find((g) => g.key === gwKey) ?? gateways.find((g) => g.presence === 'online') ?? gateways[0];
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [res, setRes] = useState<DiscoveryResult | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [user, setUser] = useState('admin');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [results, setResults] = useState<Record<string, { result: OnvifAddResult; channel?: number; codec?: string; detail?: string }>>({});
  const isOwner = !!homes.find((h) => h.home_id === gw?.homeId && h.role === 'owner');

  async function discover() {
    if (!gw) return;
    setBusy(true);
    setErr(null);
    setRes(null);
    setPicked(new Set());
    setResults({});
    try {
      setRes(await onvifApi.discover(gw.apiEndpoint, gw.homeId, gw.aid));
    } catch (e) {
      setErr(e instanceof KinioError ? `${e.code}${e.message ? ' · ' + e.message : ''}` : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function addPicked(suspect = false) {
    if (!gw || !res) return;
    setBusy(true);
    const all = [...res.groups.third_party, ...res.groups.suspect];
    for (const host of picked) {
      const c = all.find((x) => x.host === host);
      if (!c) continue;
      const isSuspect = res.groups.suspect.includes(c);
      if (isSuspect !== suspect) continue;
      try {
        const r = await onvifApi.add(gw.apiEndpoint, gw.homeId, {
          gateway: gw.aid,
          host: c.host,
          port: c.port || 80,
          user,
          password,
          name: c.name || c.model,
          ...(isSuspect ? { suspect: true, force_reason: reason } : {}),
        });
        setResults((m) => ({ ...m, [host]: r }));
      } catch (e) {
        setResults((m) => ({ ...m, [host]: { result: 'unsupported', detail: e instanceof KinioError ? e.code : String(e) } }));
      }
    }
    setPassword(''); // 帳密不留在畫面上
    setBusy(false);
    await qc.invalidateQueries({ queryKey: ['home'] });
  }

  const toggle = (host: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(host)) n.delete(host);
      else n.add(host);
      return n;
    });

  const row = (g: (typeof GROUPS)[number], c: DiscoveredCam) => {
    const r = results[c.host];
    const selectable = g === 'third_party' || (g === 'suspect' && isOwner);
    return (
      <li key={`${g}-${c.host}-${c.endpoint}`} className="onvif-row" data-group={g}>
        {selectable && <input type="checkbox" checked={picked.has(c.host)} onChange={() => toggle(c.host)} aria-label={c.host} />}
        <span className="mono">{c.host}</span>
        <span>
          {c.manufacturer} {c.model}
        </span>
        {c.evidence.length > 0 && <span className="badge">{c.evidence.join(' · ')}</span>}
        {c.channel !== undefined && <span className="muted small">{t('onvif.on_channel', { ch: c.channel + 1 })}</span>}
        {g === 'own' && <span className="muted small">{t('onvif.pair_kinio')}</span>}
        {r && (
          <span className={`badge onvif-${r.result}`}>
            {t(`onvif.result_${r.result}`)}
            {r.channel !== undefined ? ` · CH${r.channel + 1}` : ''}
            {r.codec === 'h265_only' ? ` · ${t('onvif.h265_only')}` : ''}
          </span>
        )}
      </li>
    );
  };

  const pickedThird = res ? res.groups.third_party.filter((c) => picked.has(c.host)).length : 0;
  const pickedSuspect = res ? res.groups.suspect.filter((c) => picked.has(c.host)).length : 0;

  return (
    <Dialog open={open} title={t('onvif.title')} onClose={close} wide>
      <div className="form">
        <p className="muted small">{t('onvif.help')}</p>
        {!gateways.length ? (
          <p className="notice">{t('onvif.no_gateway')}</p>
        ) : (
          <div className="toolbar">
            <select className="input" value={gw?.key ?? ''} onChange={(e) => setGwKey(e.target.value)} aria-label={t('onvif.gateway')}>
              {gateways.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.name} · {g.homeName} {g.presence === 'online' ? '' : t('common.paren', { x: t('devices.offline') })}
                </option>
              ))}
            </select>
            <button className="btn btn-primary" onClick={() => void discover()} disabled={busy || !gw || gw.presence !== 'online'}>
              {busy && !res ? t('onvif.discovering') : t('onvif.discover')}
            </button>
          </div>
        )}
        {err && <p className="error">{err}</p>}
        {res &&
          GROUPS.map((g) => (
            <fieldset key={g} className="field">
              <legend>
                {t(`onvif.group_${g}`)}{t('common.paren', { x: res.groups[g].length })}
              </legend>
              {res.groups[g].length === 0 ? <p className="muted small">{t('onvif.none')}</p> : <ul className="onvif-list">{res.groups[g].map((c) => row(g, c))}</ul>}
              {g === 'suspect' && res.groups.suspect.length > 0 && <p className="muted small">{isOwner ? t('onvif.suspect_help_owner') : t('onvif.suspect_help')}</p>}
            </fieldset>
          ))}
        {res && res.duplicates.length > 0 && <p className="muted small">{t('onvif.duplicates', { n: res.duplicates.length })}</p>}
        {res && (pickedThird > 0 || pickedSuspect > 0) && (
          <div className="card">
            <p className="small">{t('onvif.creds_help')}</p>
            <div className="toolbar">
              <input className="input" autoComplete="off" value={user} onChange={(e) => setUser(e.target.value)} placeholder={t('onvif.user')} aria-label={t('onvif.user')} />
              <PasswordInput
                className="input"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('onvif.password')}
                aria-label={t('onvif.password')}
              />
            </div>
            {pickedThird > 0 && (
              <button className="btn btn-primary" onClick={() => void addPicked(false)} disabled={busy || !user}>
                {t('onvif.add', { n: pickedThird })}
              </button>
            )}
            {pickedSuspect > 0 && (
              <>
                <textarea className="input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('onvif.reason')} aria-label={t('onvif.reason')} />
                <button className="btn" onClick={() => void addPicked(true)} disabled={busy || reason.trim().length < 4}>
                  {t('onvif.force_add', { n: pickedSuspect })}
                </button>
              </>
            )}
          </div>
        )}
        {/* 只有 ✕ 不夠明顯：底部補〔取消〕（第三輪 R3-P2-5） */}
        <div className="dialog-actions">
          <button className="btn" onClick={close} data-testid="onvif-cancel">
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
